/**
 * The ONE right-side slide-over (WS-Z7 / R1), promoted out of `admin/Drawer.tsx`.
 *
 * It was an admin-console local for three admin panels; the inventory then found two more
 * hand-rolled drawers in `observability/` and a `.lightbox` carrying `role="dialog"` in
 * `templates/`. A detail view is a drawer, and there is now one of them, in the shared layer
 * where the surfaces that need it can reach it. `.lightbox` goes back to being image zoom.
 *
 * The chrome — focus in, focus back, Tab trapped, Escape, the heading as the accessible
 * name — is `ui/dialog.ts`, shared verbatim with `ui/Modal.tsx`. What is left here is the
 * only thing that is genuinely a drawer: a panel on the right, with a header / body /
 * footer the caller fills. Layout classes live in `styles/components.css` (`.drawer*`).
 */
import type { ReactNode } from "react";
import { Icon } from "./Icon.js";
import { DialogStackProvider, useDialogChrome } from "./dialog.js";

export interface DrawerProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Status pill shown top-right, before the close button. */
  badge?: ReactNode;
  /** Action bar pinned to the bottom of the drawer — an `ActionBar` belongs here. */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  testid?: string;
  /**
   * The close control's own `data-testid`, defaulting to `drawer-close` (WS-Z8).
   *
   * Owner rule A is "remove redundancy, never capability", and a `data-testid` a suite names
   * is capability: `templates/TemplateListPage.tsx` came to this primitive carrying
   * `customer-template-preview-close`, which 2 assertions in `customer-templates.test.tsx`
   * and 1 in `browse-page.test.tsx` reach for by name — including the one that says focus
   * lands on it when the drawer opens. The alternative was a second Close button in the
   * footer, which is the duplicate the same rule forbids. Every existing caller omits it and
   * keeps `drawer-close` unchanged.
   */
  closeTestid?: string;
}

export function Drawer({ title, subtitle, badge, footer, onClose, children, testid, closeTestid = "drawer-close" }: DrawerProps) {
  const { panelRef, closeRef, titleId, onOverlayClick, dialog } = useDialogChrome(onClose);

  return (
    // Everything this drawer renders is rendered INSIDE it, so a dialog opened from inside
    // it orders above it — including one whose panel is portaled elsewhere (review-2 F-10).
    <DialogStackProvider dialog={dialog}>
    <div className="drawer" role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid={testid}>
      <div className="drawer__overlay" onClick={onOverlayClick} aria-hidden="true" />
      <div className="drawer__panel" ref={panelRef}>
        <header className="drawer__head">
          <div className="drawer__heading">
            <h2 className="drawer__title" id={titleId}>{title}</h2>
            {subtitle && <p className="drawer__subtitle">{subtitle}</p>}
          </div>
          <div className="drawer__head-actions">
            {badge}
            <button
              ref={closeRef}
              type="button"
              className="drawer__close"
              onClick={onClose}
              data-testid={closeTestid}
              aria-label="Close"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </header>
        <div className="drawer__body">{children}</div>
        {footer && <footer className="drawer__footer" data-testid="drawer-footer">{footer}</footer>}
      </div>
    </div>
    </DialogStackProvider>
  );
}
